import { useSelector, useDispatch } from 'react-redux';

import {
  PAGINATION_ACTIONS,
} from './constants';
import { PaginationReducerType } from 'src/reducers/paginationReducers';
import { PaginationActions, PaginationData } from 'src/Library/Types';

export type usePaginationReturnType = {
  readonly state: {
    appliedFilters: any,
    pagination: PaginationData,
    data: any,
    isLoading: boolean,
    errorMessage: string,
  };
  readonly actions: PaginationActions;
};

/**
 * Be sure to make an entry in 'PAGINATION_REDUCERS' (createPaginationReducers) before using it.
 */
const usePagination = ({
  paginationReducer,
  fetchAPI,
}: {
  [key: string]: any,
  paginationReducer: PaginationReducerType,
  fetchAPI: (filterParams: {}) => void;
}) : usePaginationReturnType => {
  const dispatch = useDispatch();
  const appliedFilters = useSelector(state => state[paginationReducer.name].appliedFilters);
  const pagination = useSelector(state => state[paginationReducer.name].pagination);
  const data = useSelector(state => state[paginationReducer.name].data);
  const isLoading = useSelector(state => state[paginationReducer.name].isLoading);
  const errorMessage = useSelector(state => state[paginationReducer.name].errorMessage);

  const setFilter = async (val, shouldApplyFilter = false) => {
    dispatch({
      type: PAGINATION_ACTIONS(paginationReducer.label).SET_FILTER,
      data: val,
    });
    if (shouldApplyFilter) {
      applyFilter();
    }
  };

  const cancelFilter = async () => {
    dispatch({
      type: PAGINATION_ACTIONS(paginationReducer.label).CANCEL_FILTER,
    });
  };

  const applyFilter = async (noRender = false) => {
    dispatch({
      type: PAGINATION_ACTIONS(paginationReducer.label).APPLY_FILTER,
    });
    if (!noRender) {
      dispatch(fetchData());
    }
  };

  const resetFilter = async (hitApi = false) => {
    dispatch({
      type: PAGINATION_ACTIONS(paginationReducer.label).RESET_FILTER,
    });
    if (!hitApi) {
      dispatch(fetchData());
    }
  };

  const setPagination = async (val) => {
    dispatch({
      type: PAGINATION_ACTIONS(paginationReducer.label).SET_PAGINATION,
      data: val,
    });
    dispatch(fetchData());
  };

  const setPrevPagination = async () => {
    dispatch({
      type: PAGINATION_ACTIONS(paginationReducer.label).SET_PREV_PAGINATION,
    });
    dispatch(fetchData());
  };

  const fetchData = () => async (dispatch, getState) => {
    dispatch({
      type: PAGINATION_ACTIONS(paginationReducer.label).ENABLE_LOADING,
    });

    const reducerState = getState()[paginationReducer.name];
    const filterParams = {
      ...reducerState.appliedFilters,
      ...reducerState.pagination,
      resultPerPage: Number(reducerState.pagination.resultPerPage),
      lastRowId: getLastRowId(reducerState.pagination) || null,
      lastSortedColumnValue: getLastSortedColumnValue(reducerState.pagination) || null,
    };

    const response: any = await fetchAPI(filterParams);
    if (response.isSuccess) {
      dispatch({
        type: PAGINATION_ACTIONS(paginationReducer.label).PAGINATION_LOADED,
        data: response.data,
      });
    } else {
      dispatch({
        type: PAGINATION_ACTIONS(paginationReducer.label).PAGINATION_ERROR,
        errorMessage: response.errorMessage,
      });
    }

    dispatch({
      type: PAGINATION_ACTIONS(paginationReducer.label).DISABLE_LOADING,
    });
  };
  const getLastRowId = (pagination) => {
    switch (pagination.nextOrPrev) {
      case 'prev':
        return pagination.lastItemIdPrev;
      case 'next':
        return pagination.lastItemIdNext;
      default:
        return null;
    }
  };

  const getLastSortedColumnValue = (pagination) => {
    switch (pagination.nextOrPrev) {
      case 'prev':
        return pagination.lastSortedColumnValuePrev;
      case 'next':
        return pagination.lastSortedColumnValueNext;
      default:
        return null;
    }
  };

  return {
    state: {
      appliedFilters,
      pagination,
      data,
      isLoading,
      errorMessage,
    },
    actions: {
      setFilter,
      cancelFilter,
      applyFilter,
      resetFilter,
      setPagination,
      setPrevPagination,
      reload: setPrevPagination,
    },
  };
};

export default usePagination;
